/**
 * Pure helpers for persisting a SDMS cell's value to SpreadSheetEntry_SampleInfo — extracted
 * from SdmsUniverSheet.tsx so they are unit-testable without the heavy Univer module.
 * (OG parity, 2026-09-15; see persistText's doc + [[sdms-handover-2026-09-15]] §4a.)
 */

/** Raw scalar text of an ExcelJS/Univer cell value (no number-format applied) —
 *  used for a format-noise-free change compare. */
export function rawStr(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (o.result != null) return String(o.result);
    if (o.text != null) return String(o.text);
    if (Array.isArray(o.richText)) return (o.richText as Array<{ text?: string }>).map(t => t.text ?? '').join('');
    return '';
  }
  return String(v);
}

/** A LIVE (Univer) cell value as a date on the UTC axis ExcelJS uses, or null: a number is an Excel
 *  serial (how a loaded date cell sits in Univer); a string is a date the import / analyst typed —
 *  `yyyy-MM-dd[ HH:mm[:ss]]` (the instrument parser's shape) or `MM/DD/YYYY[ HH:mm[:ss]]`, the year also
 *  as two digits — what a `m/d/yy h:mm` cell SHOWS (PFAS 533 AnalysisDate "10/15/25 14:38"). A two-digit
 *  year reads as .NET's DateTime.Parse does (≤ 49 → 20yy, else 19yy), the parse OG's save applies to it. */
export function liveDateOf(v: unknown): Date | null {
  if (typeof v === 'number' && Number.isFinite(v)) return new Date(Date.UTC(1899, 11, 30) + Math.round(v * 86_400_000));
  if (typeof v !== 'string') return null;
  const t = v.trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(t);
  if (m) return new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0)));
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(t);
  if (!m) return null;
  const year = m[3]!.length === 2 ? (+m[3]! <= 49 ? 2000 : 1900) + +m[3]! : +m[3]!;
  return new Date(Date.UTC(year, +m[1]! - 1, +m[2]!, +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0)));
}

/** True when a cell's LIVE formula is a different formula from the one stored in the workbook —
 *  compared without a leading `=`, whitespace, case or the OOXML `_xlfn.` / `_xlws.` function prefixes,
 *  so Univer's own re-spelling of the same formula never counts, while a real change does (the import
 *  splicing `VALUE("")` → `VALUE(9.56)`). */
export function formulaDiffers(stored: string, live: string): boolean {
  const norm = (f: string) => f.replace(/^\s*=/, '').replace(/_xl(fn|ws)\./gi, '').replace(/\s+/g, '').toUpperCase();
  return norm(stored) !== norm(live);
}

/** An Excel / Univer error value (#DIV/0!, #VALUE!, #REF!, #NAME?, #N/A, #NUM!, #NULL!, #SPILL!, #CALC!,
 *  #GETTING_DATA). OG saves through a DevExpress DataTableExporter whose conversion-error handler stores NULL
 *  for one, so it is never persisted — AI stored "#DIV/0!" as the ISTD rows' QCStatus (OG vs AI VOC
 *  comparison 2026-09-28, defect 3). The server applies the same rule (AnalysisWriter.IsSheetErrorValue). */
export function isSheetError(v: unknown): boolean {
  return typeof v === 'string' && /^#(DIV\/0!|VALUE!|REF!|NAME\?|N\/A|NUM!|NULL!|SPILL!|CALC!|GETTING_DATA)$/i.test(v.trim());
}

/** The decimals a number format shows (`0.00` → 2, `#,##0` → 0), or null for General / text (`@`). */
export function decimalsFromNumFmt(fmt: string | undefined): number | null {
  if (!fmt || fmt === 'General' || fmt === '@') return null;
  const sect = fmt.split(';')[0] ?? '';
  const dot = sect.indexOf('.'); if (dot < 0) return 0;
  let n = 0; for (let i = dot + 1; i < sect.length; i++) { const ch = sect[i]; if (ch === '0' || ch === '#') n++; else break; }
  return n;
}

/** `v` to `d` decimals rounded half AWAY FROM ZERO on its DECIMAL value, as Excel / DevExpress show it.
 *  `toFixed` rounds the binary double, so 89.55 (stored as 89.5499…) gave "89.5" where OG shows "89.6"
 *  (LCS %Recovery, OG vs AI VOC comparison 2026-09-29). The value is first trimmed to 15 significant
 *  digits (float noise: 8.300000000000001 → 8.3), then shifted by its decimal string. */
function toFixedHalfUp(v: number, d: number): string {
  const x = parseFloat(v.toPrecision(15));
  const txt = String(Math.abs(x));
  if (/e/i.test(txt)) return x.toFixed(d);   // exponent notation (very large / tiny) — no decimal shift
  const shifted = Math.round(Number(`${txt}e${d}`));
  return (Math.sign(x) * Number(`${shifted}e-${d}`)).toFixed(d);
}

/** A number rendered through its cell's number format — what the sheet shows. */
export function fmtNumber(v: number, fmt: string | undefined): string {
  // Kill IEEE float noise before rounding a percent: 0.895*100 = 89.49999999999999
  // would toFixed to "89", but Excel shows 90% (89.5 rounds half-away-from-zero).
  if (fmt && fmt.includes('%')) { const pd = decimalsFromNumFmt(fmt) ?? 0; return `${toFixedHalfUp(parseFloat((v * 100).toPrecision(12)), pd)}%`; }
  const d = decimalsFromNumFmt(fmt);
  if (d == null) return String(parseFloat(v.toPrecision(12)));
  const s = toFixedHalfUp(v, d);
  if (fmt && fmt.includes(',')) { const [int, dec] = s.split('.'); const grp = (int ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ','); return dec ? `${grp}.${dec}` : grp; }
  return s;
}

/** True for a date / time number format (its d/m/y/h/s tokens outside quotes, escapes and [..] blocks). */
export function isDateNumFmt(fmt: string | undefined): boolean {
  if (!fmt || fmt === 'General' || fmt === '@') return false;
  const bare = fmt.replace(/"[^"]*"/g, '').replace(/\[[^\]]*\]/g, '').replace(/\\./g, '');
  return /[dmyhs]/i.test(bare.split(';')[0] ?? '');
}

/** The text written to SpreadSheetEntry_SampleInfo for a sheet cell — what the sheet SHOWS.
 *  OG's current save (SpreadsheetEntryController :13766-13803, the 2026-09-23 build) exports a column
 *  as TEXT whenever a numeric cell's display differs from its raw number, then puts each numeric cell's
 *  DisplayText there ("export the value exactly as shown in the sheet … keep format trailing zeros"), so
 *  a Numeric Result 9.8 in a 0.00 cell is stored "9.80" and an ISTD ratio 1.687063 in a 0.00 cell
 *  "1.69" — AI stored the raw "9.8" / "1.687063…" (OG vs AI VOC comparison 2026-09-28, defect 9). This
 *  replaces the older rule (2026-09-15, the OG build before) that stripped the format padding.
 *  A number with a decimal / percent format is rendered through it (`numFmt`); a General number and a
 *  date serial keep their raw text; text ("Fail", "<0.1", a rounded "50.0") is kept as it is; an error
 *  is no value (OG stores NULL). */
export function persistText(now: unknown, numFmt?: string): string {
  if (now != null && typeof now === 'object' && !(now instanceof Date)) {
    const o = now as Record<string, unknown>;
    if (o.result !== undefined && o.result !== null) return persistText(o.result, numFmt);
  }
  if (typeof now === 'number')
    return Number.isFinite(now) && decimalsFromNumFmt(numFmt) != null && !isDateNumFmt(numFmt)
      ? fmtNumber(now, numFmt) : String(now);
  const s = rawStr(now);
  return isSheetError(s) ? '' : s;
}

/** An imported value bound for a DATE cell, as the Excel serial a real date holds — or null when the cell is not a
 *  date cell or the value is no date. OG's rowset ANALYZEDDATE column is DateTime-typed (QCBatchController :8702),
 *  so its re-bind puts a real date in the cell; AI wrote the import's text ("2026-09-02 08:18"), and every formula
 *  reading it (VOC ACQTIME = $AY$8) then cached TEXT (OG vs AI comparison 2026-09-28, point 12). */
export function importDateSerial(value: string, cellValue: unknown, numFmt: string | undefined): number | null {
  if (!(cellValue instanceof Date) && !isDateNumFmt(numFmt)) return null;
  const d = liveDateOf(value);
  return d ? (d.getTime() - Date.UTC(1899, 11, 30)) / 86_400_000 : null;
}
